import React, { useState } from 'react';
import {
  ProcessDefinition,
  ProcessTransition,
  ProcessRole,
  AddArtifactData,
} from '../../types/processDefinition';
import { ProcessArtifactGroup } from './ProcessArtifactGroup';
import { AddArtifactDialog } from './AddArtifactDialog';
import './ProcessArtifactGroups.css';

interface ProcessArtifactGroupsProps {
  process: ProcessDefinition;
  onAddArtifact: (artifactData: AddArtifactData) => void;
  onUpdateTransition: (transitionId: string, transitionData: Record<string, unknown>) => void;
  onDeleteTransition: (transitionId: string) => void;
  onNavigateToProcess: (processId: string) => void;
}

export const ProcessArtifactGroups: React.FC<ProcessArtifactGroupsProps> = ({
  process,
  onAddArtifact,
  onUpdateTransition,
  onDeleteTransition,
  onNavigateToProcess,
}) => {
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Group transitions by artifact
  const artifactGroups =
    process.transitions?.reduce(
      (groups: { [key: string]: ProcessTransition[] }, transition: ProcessTransition) => {
        const artifactId = transition.prime_artifact_ref;
        if (!groups[artifactId]) {
          groups[artifactId] = [];
        }
        groups[artifactId].push(transition);
        return groups;
      },
      {}
    ) || {};

  const handleAddArtifact = (artifactData: AddArtifactData) => {
    onAddArtifact(artifactData);
    setShowAddDialog(false);
  };

  const getRoleGroups = () => {
    const roleGroups: { [key in ProcessRole]: ProcessTransition[] } = {
      PROCESSITEM: [],
      MATERIAL: [],
      RESOURCE: [],
      PROCESSITEM_PERSISTENT: [],
      CREATION: [],
    };

    Object.values(artifactGroups)
      .flat()
      .forEach((transition: ProcessTransition) => {
        roleGroups[transition.default_transition_type].push(transition);
      });

    return roleGroups;
  };

  const roleGroups = getRoleGroups();

  return (
    <div className="process-artifact-groups">
      <div className="groups-header">
        <h2>Process Artifacts</h2>
        <button onClick={() => setShowAddDialog(true)} className="add-artifact-btn">
          + Add Artifact
        </button>
      </div>

      <div className="artifact-role-groups">
        {Object.entries(roleGroups).map(([role, transitions]) => {
          if (transitions.length === 0) return null;

          return (
            <div key={role} className="role-group">
              <h3 className="role-title">
                {role.replace('_', ' ')}
                <span className="role-count">({transitions.length})</span>
              </h3>

              <div className="artifact-groups">
                {Object.entries(artifactGroups).map(([artifactId, artifactTransitions]) => {
                  const roleTransitions = (artifactTransitions as ProcessTransition[]).filter(
                    (t: ProcessTransition) => t.default_transition_type === role
                  );
                  if (roleTransitions.length === 0) return null;

                  return (
                    <ProcessArtifactGroup
                      key={artifactId}
                      artifactId={artifactId}
                      transitions={roleTransitions}
                      role={role as ProcessRole}
                      onUpdateTransition={onUpdateTransition}
                      onDeleteTransition={onDeleteTransition}
                      onNavigateToProcess={onNavigateToProcess}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {Object.values(roleGroups).flat().length === 0 && (
        <div className="empty-state">
          <p>No artifacts have been added to this process yet.</p>
          <button onClick={() => setShowAddDialog(true)} className="add-first-artifact-btn">
            Add First Artifact
          </button>
        </div>
      )}

      {showAddDialog && (
        <AddArtifactDialog
          processId={process.id}
          onSave={handleAddArtifact}
          onCancel={() => setShowAddDialog(false)}
        />
      )}
    </div>
  );
};
